import { useEffect, useId, useRef } from 'react';

import { Button } from './Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';

export interface ConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel: string;
  /** 'destructive' troca a cor do botão de confirmar para danger. */
  variant?: 'default' | 'destructive';
  onConfirm: () => void;
  /** Desabilita o botão de confirmar (ex.: durante submit assíncrono). */
  busy?: boolean;
  /**
   * Recusa do servidor à confirmação, exibida **dentro** do modal.
   *
   * Por quê aqui: enquanto o modal está aberto, o overlay cobre a página e o
   * Radix marca o resto da árvore com `aria-hidden` — mensagem escrita lá fica
   * esmaecida atrás do overlay e invisível para leitor de tela, e o usuário só
   * vê a confirmação sem efeito (F-E61.F2-01).
   *
   * É `role="alert"` — anunciada quando aparece — e descreve o botão de
   * confirmar, para quem chega nele depois do anúncio. A descrição do modal
   * fica com o Radix: sobrescrever o `aria-describedby` do conteúdo derruba a
   * ligação que ele monta sozinho.
   */
  error?: string;
}

/**
 * Confirmação com foco padrão no botão **seguro** (cancelar).
 *
 * Por quê: Esc fecha (Radix). Enter, com o foco no cancelar, **cancela** — não
 * confirma acidentalmente. Para confirmar é preciso mover o foco para o botão
 * de confirmar (Tab) e então pressionar Enter, ou clicar.
 */
export function ConfirmModal({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  onConfirm,
  busy = false,
  error,
}: ConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const errorId = useId();

  useEffect(() => {
    if (open) {
      // setTimeout = espera Radix montar o portal antes do focus().
      const t = window.setTimeout(() => cancelRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          // Anula o auto-focus padrão do Radix (que iria no primeiro focusable);
          // queremos focar o botão de cancelar explicitamente no useEffect.
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {error != null && (
          <p
            id={errorId}
            role="alert"
            data-testid="confirm-modal-error"
            className="mt-4 border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
          >
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            ref={cancelRef}
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
            aria-describedby={error != null ? errorId : undefined}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
